import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";

import { getFlexDeskLog, getProvisioningPhones, getSippeers } from "@/api/stackApi";
import { HELP_LOGS_FLEX_DESKS } from "@/constants/HelpUrls";
import Avatar from "@/design/components/Avatar";
import FabBar from "@/design/components/FabBar";
import MessageStack from "@/design/components/MessageStack";
import Page from "@/design/layouts/Page";
import { setHelpUrl } from "@/store/reducers/ui";
import useAccessibleColors from "@/utils/hooks/useAccessibleColors";
import useNow from "@/utils/hooks/useNow";
import { formatToMac } from "@/utils/utility";
import Valkyrie, {
  viArrowRightFromBracket,
  viArrowRightToBracket,
  viCalendar,
  viCircleXmark,
  viPerson,
  viPersonClock,
  viPhoneOffice,
} from "@sippy-platform/valkyrie";

import { Box, Card, CircularProgress, Container, Divider, Pagination, Paper } from "@mui/material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  differenceInDays,
  differenceInSeconds,
  endOfDay,
  format,
  isValid,
  isWithinInterval,
  parseISO,
  startOfDay,
} from "date-fns";

import ClearFilterAction from "../_Components/ClearFilterAction";
import DataCell from "../_Components/DataCell";
import DateFilter from "../_Components/DateFilter";
import FilterBar from "../_Components/FilterBar";
import FilterDropdown from "../_Components/FilterDropdown";
import RefreshActions from "../_Components/RefreshActions";
import TableHead from "../_Components/TableHead";
import TableHeading from "../_Components/TableHeading";
import TableRow from "../_Components/TableRow";

export default function FlexDesKLog() {
  const colors = useAccessibleColors();
  const dispatch = useDispatch();

  const dateFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS";

  const [searchParams, setSearchParams] = useSearchParams();

  const pbx = useSelector((state) => state.domain.pbx);
  const logSettings = useSelector((state) => state.settings.logs);

  useEffect(() => {
    dispatch(setHelpUrl(HELP_LOGS_FLEX_DESKS));
  }, [dispatch]);

  // Filter preparations
  const sippeersQuery = useQuery({
    queryKey: [pbx.context, "sippeers"],
    queryFn: getSippeers,
    refetchInterval: 10 * 1000 * 60,
  });
  const provisioningPhonesQuery = useQuery({
    queryKey: [pbx.context, "provisioning.phones"],
    queryFn: getProvisioningPhones,
    refetchInterval: 10 * 1000 * 60,
  });

  // Filters
  const now = useNow();
  const { page, order, orderBy, startDate, endDate, user, mac } = useMemo(
    () => ({
      page: Number(searchParams.get("page") ?? 0),
      order: searchParams.get("order") === "asc" ? "asc" : "desc",
      orderBy: ["date", "device", "name", "event"].includes(searchParams.get("orderBy"))
        ? searchParams.get("orderBy")
        : "date",
      startDate:
        searchParams.get("startDate") && isValid(parseISO(searchParams.get("startDate")))
          ? searchParams.get("startDate")
          : format(startOfDay(now), dateFormat),
      endDate:
        searchParams.get("endDate") && isValid(parseISO(searchParams.get("endDate")))
          ? searchParams.get("endDate")
          : format(endOfDay(now), dateFormat),
      user: sippeersQuery.data?.find((user) => searchParams.get("user") === (user.value ?? user.exten))
        ? searchParams.get("user")
        : "",
      mac: provisioningPhonesQuery.data?.find((device) => searchParams.get("mac") === device.mac)
        ? searchParams.get("mac")
        : "",
    }),
    [now, provisioningPhonesQuery.data, searchParams, sippeersQuery.data],
  );

  const isFiltering = useMemo(
    () =>
      !(
        page === 0 &&
        order === "desc" &&
        orderBy === "date" &&
        startDate === format(startOfDay(now), dateFormat) &&
        endDate === format(endOfDay(now), dateFormat) &&
        user === "" &&
        mac === ""
      ),
    [now, page, order, orderBy, startDate, endDate, user, mac],
  );

  const setFilter = useCallback(
    (key, value, resetPage = true) => {
      searchParams.set(key, value);
      if (resetPage) searchParams.set("page", 0);

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  // Check if the selected date range is invalid (either more than 31 days + 1 hour (because DST) or non-positive duration)
  const invalidDateRange =
    differenceInSeconds(parseISO(endDate), parseISO(startDate)) > 31 * 24 * 60 * 60 + 60 * 60 ||
    differenceInSeconds(parseISO(endDate), parseISO(startDate)) <= 0;

  // Query
  const pauseRefresh = !isWithinInterval(now, { start: parseISO(startDate), end: parseISO(endDate) });
  const flexDeskLogQuery = useQuery({
    queryKey: [pbx.context, "log.flexDesks", page, order, orderBy, startDate, endDate, user, mac],
    queryFn: () =>
      getFlexDeskLog({
        page,
        order,
        orderBy,
        startDate,
        endDate,
        number: user,
        mac,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: pauseRefresh ? 0 : logSettings.refreshInterval * 1000 * 60,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: pauseRefresh || logSettings.refreshInterval === 0 ? false : true,
    enabled:
      isValid(parseISO(startDate)) &&
      isValid(parseISO(endDate)) &&
      differenceInDays(parseISO(endDate), parseISO(startDate)) <= 31,
  });

  // Handlers
  const handlePage = (event, page) => {
    setFilter("page", page - 1, false);
  };

  return (
    <Page sx={{ pt: 8, position: "relative" }}>
      <title>{`Flex desk log \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>

      <FilterBar>
        <ClearFilterAction disabled={isFiltering} />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <DateFilter startDate={startDate} endDate={endDate} setFilter={setFilter} />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <FilterDropdown
          id="mac"
          icon={viPhoneOffice}
          options={provisioningPhonesQuery.data || []}
          defaultOptions={[{ name: "All devices", value: "" }]}
          value={mac}
          setValue={(value) => setFilter("mac", value)}
          getOptionLabel={(x) => (x.mac ? formatToMac(x.mac) : x.name)}
          getOptionLabelSecondary={(x) => x.flexBot?.name}
          getOptionValue={(x) => x.mac ?? x.value}
          searchKeys={[
            "mac",
            "type",
            "phone",
            "sippeer.name",
            "sippeer.exten",
            "flexBot.name",
            "flexUser.name",
            "flexUser.exten",
            "serialNumber",
          ]}
        />
        <FilterDropdown
          id="user"
          icon={viPerson}
          options={sippeersQuery.data || []}
          defaultOptions={[{ name: "All users", value: "" }]}
          value={user}
          setValue={(value) => setFilter("user", value)}
          getOptionLabel={(x) => x.name}
          getOptionLabelSecondary={(x) => x.exten}
          getOptionValue={(x) => x.value ?? x.exten}
          searchKeys={["name", "exten"]}
        />
        <Box sx={{ flexGrow: 1 }} />
        <RefreshActions
          onClick={() => flexDeskLogQuery.refetch()}
          isRefreshing={flexDeskLogQuery.isFetching}
          isPaused={pauseRefresh}
        />
      </FilterBar>

      <Container maxWidth="xl">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, auto)",
            gridTemplateRows: "auto auto",
            gap: 1,
          }}
        >
          <TableHead>
            <TableHeading id="date" setFilter={setFilter} order={order} orderBy={orderBy}>
              Time
            </TableHeading>
            <TableHeading id="device" setFilter={setFilter} order={order} orderBy={orderBy}>
              Device
            </TableHeading>
            <TableHeading id="name" setFilter={setFilter} order={order} orderBy={orderBy}>
              User
            </TableHeading>
            <TableHeading id="event" setFilter={setFilter} order={order} orderBy={orderBy}>
              Event
            </TableHeading>
          </TableHead>
          {invalidDateRange ? (
            <MessageStack icon={<Valkyrie icon={viCalendar} />} message="The current date range is not valid." />
          ) : flexDeskLogQuery.isFetched ? (
            <>
              {flexDeskLogQuery.data?.fdl?.length >= 1 ? (
                <Card sx={{ display: "grid", gridTemplateColumns: "subgrid", gridColumn: "1 / -1", mb: 6.5 }}>
                  {flexDeskLogQuery.data?.fdl.map((flexEvent, eventKey) => (
                    <TableRow key={eventKey}>
                      <DataCell
                        invertOrder
                        noWrap
                        tabular
                        primary={format(flexEvent.date, "pp")}
                        secondary={format(flexEvent.date, "PPP")}
                      />
                      <DataCell
                        startDecorator={<Avatar text={formatToMac(flexEvent.device)} px={24} icon={viPhoneOffice} />}
                        primary={formatToMac(flexEvent.device)}
                      />
                      <DataCell
                        startDecorator={<Avatar text={flexEvent.name} px={24} />}
                        primary={flexEvent.name}
                        secondary={flexEvent.number}
                      />
                      <DataCell
                        startDecorator={
                          <Valkyrie
                            icon={flexEvent.event === "LOGIN" ? viArrowRightToBracket : viArrowRightFromBracket}
                            style={{ color: flexEvent.event === "LOGIN" ? colors.green : colors.red }}
                          />
                        }
                        primary={flexEvent.event === "LOGIN" ? "Login" : "Logout"}
                      />
                    </TableRow>
                  ))}
                </Card>
              ) : (
                <MessageStack icon={<Valkyrie icon={viPersonClock} />} message="No data for your current filters..." />
              )}
            </>
          ) : flexDeskLogQuery.isError ? (
            <MessageStack
              icon={<Valkyrie icon={viCircleXmark} />}
              color="error"
              message="We couldn't finish your request..."
              description={flexDeskLogQuery?.error?.message}
            />
          ) : (
            <MessageStack icon={<CircularProgress size={36} />} message="Getting your data..." />
          )}
        </Box>

        <FabBar sx={{ px: 1, justifyContent: "center" }}>
          <Paper
            variant="acrylic"
            sx={{
              whiteSpace: "nowrap",
              display: {
                xs: "none",
                md: "flex",
              },
              alignItems: "center",
              justifyContent: "center",
              px: 1.5,
              py: 0.75,
            }}
          >
            <Box>
              {page * 50 + 1}-
              {(page + 1) * 50 > flexDeskLogQuery.data?.count ? flexDeskLogQuery.data?.count : (page + 1) * 50} of{" "}
              {flexDeskLogQuery.data?.count}
            </Box>
          </Paper>
          {Math.ceil(flexDeskLogQuery.data?.count / 50) > 1 && (
            <Pagination count={Math.ceil(flexDeskLogQuery.data?.count / 50)} page={page + 1} onChange={handlePage} />
          )}
        </FabBar>
      </Container>
    </Page>
  );
}
